import { useQuery, useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { t } from 'i18next';
import {
  ChevronDown,
  EllipsisVertical,
  Import,
  MessageSquareText,
  Plus,
  Workflow,
  Sparkle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useEmbedding, useNewWindow } from '@/components/embed-provider';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DataTable, RowDataWithActions } from '@/components/ui/data-table';
import { DataTableColumnHeader } from '@/components/ui/data-table/data-table-column-header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PermissionNeededTooltip } from '@/components/ui/permission-needed-tooltip';
import { showErrorToast } from '@/components/ui/use-toast';
import { FlowStatusToggle } from '@/features/flows/components/flow-status-toggle';
import { ImportFlowDialog } from '@/features/flows/components/import-flow-dialog';
import { SelectFlowTemplateDialog } from '@/features/flows/components/select-flow-template-dialog';
import { FeedbackPopover } from '@/features/feedback/components/feedback-popover';
import { flowsApi } from '@/features/flows/lib/flows-api';
import { FolderBadge } from '@/features/folders/component/folder-badge';
import {
  FolderFilterList,
  folderIdParamName,
} from '@/features/folders/component/folder-filter-list';
import { foldersApi } from '@/features/folders/lib/folders-api';
import { UsageOverview } from '@/features/usage/components/usage-overview';
import { PieceIconList } from '@/features/pieces/components/piece-icon-list';
import { useAuthorization } from '@/hooks/authorization-hooks';
import { authenticationSession } from '@/lib/authentication-session';
import { formatUtils } from '@/lib/utils';
import { FeedbackSource, FlowStatus, Permission, PopulatedFlow } from '@activepieces/shared';
import { useFlowFilters } from '@/features/flows/lib/use-flow-filters';

import FlowActionMenu from '../../../app/components/flow-actions-menu';
import { TableTitle } from '../../../components/ui/table-title';

import TaskLimitAlert from './task-limit-alert';
import { useFlowsBulkActions } from '@/features/flows/lib/use-flows-bulk-actions';
import { FlowVersionStateDot } from '@/features/flows/components/flow-version-state-dot';


const FlowsPage = () => {
  const { checkAccess } = useAuthorization();
  const doesUserHavePermissionToWriteFlow = checkAccess(Permission.WRITE_FLOW);
  const { embedState } = useEmbedding();
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const openNewWindow = useNewWindow();
  const [searchParams] = useSearchParams();
  const { filters } = useFlowFilters();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['flow-table', searchParams.toString()],
    staleTime: 0,
    queryFn: () => {
      const name = searchParams.get('name');
      const status = searchParams.getAll('status') as FlowStatus[];
      const pieces = searchParams.getAll('steps');
      const cursor = searchParams.get('cursor');
      const limit = searchParams.get('limit')
        ? parseInt(searchParams.get('limit')!)
        : 10;
      const folderId = searchParams.get('folderId') ?? undefined;

      return flowsApi.list({
        projectId: authenticationSession.getProjectId()!,
        cursor: cursor ?? undefined,
        limit,
        name: name ?? undefined,
        status,
        folderId,
        pieces: pieces.length > 0 ? pieces : undefined,
      });
    },
  });

  const { mutate: createFlow, isPending: isCreateFlowPending } = useMutation<
    PopulatedFlow,
    Error,
    { isAiBuilder: boolean }
  >({
    mutationFn: async () => {
      const folderId = searchParams.get(folderIdParamName);
      const folder =
        folderId && folderId !== 'NULL'
          ? await foldersApi.get(folderId)
          : undefined;
      const flow = await flowsApi.create({
        projectId: authenticationSession.getProjectId()!,
        displayName: t('Untitled'),
        folderName: folder?.displayName,
      });
      return flow;
    },
    onSuccess: (flow, { isAiBuilder }) => {
      navigate(`/flows/${flow.id}`, {
        state: { isAiBuilder },
      });
    },
    onError: (error) => showErrorToast(error),
  });

  const [selectedRows, setSelectedRows] = useState<Array<PopulatedFlow>>([]);

  const columns: (ColumnDef<RowDataWithActions<PopulatedFlow>> & {
    accessorKey: string;
  })[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
          }
          onCheckedChange={(value) => {
            const isChecked = !!value;
            table.toggleAllPageRowsSelected(isChecked);

            if (isChecked) {
              const allRowIds = table
                .getRowModel()
                .rows.map((row) => row.original);

              const newSelectedRowIds = [...allRowIds, ...selectedRows];

              const uniqueRowIds = Array.from(
                new Map(
                  newSelectedRowIds.map((item) => [item.id, item]),
                ).values(),
              );

              setSelectedRows(uniqueRowIds);
            } else {
              const filteredRowIds = selectedRows.filter((row) => {
                return !table
                  .getRowModel()
                  .rows.some((r) => r.original.version.id === row.version.id);
              });
              setSelectedRows(filteredRowIds);
            }
          }}
        />
      ),
      cell: ({ row }) => {
        const isChecked = selectedRows.some(
          (selectedRow) =>
            selectedRow.id === row.original.id &&
            selectedRow.status === row.original.status,
        );
        return (
          <Checkbox
            checked={isChecked}
            onCheckedChange={(value) => {
              const isChecked = !!value;
              let newSelectedRows = [...selectedRows];
              if (isChecked) {
                const exists = newSelectedRows.some(
                  (selectedRow) => selectedRow.id === row.original.id,
                );
                if (!exists) {
                  newSelectedRows.push(row.original);
                }
              } else {
                newSelectedRows = newSelectedRows.filter(
                  (selectedRow) => selectedRow.id !== row.original.id,
                );
              }
              setSelectedRows(newSelectedRows);
              row.toggleSelected(!!value);
            }}
          />
        );
      },
      accessorKey: 'select',
    },
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Name')} />
      ),
      cell: ({ row }) => {
        const { publishedVersionId, version } = row.original;
        const { id: flowId, state: flowState, displayName } = version;
        const isNotPublishedVersion = publishedVersionId && publishedVersionId !== flowId;
        return (
          <div className="flex items-center">
            <div>{displayName}</div>
            { isNotPublishedVersion && (
              <FlowVersionStateDot
                state={flowState}
                versionId={flowId}
                publishedVersionId={publishedVersionId}
              ></FlowVersionStateDot>
            )}
           </div>
        );
      },
    },
    {
      accessorKey: 'steps',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Steps')} />
      ),
      cell: ({ row }) => {
        return (
          <PieceIconList
            flowVersion={row.original.version}
            maxNumberOfIconsToShow={2}
          />
        );
      },
    },
    {
      accessorKey: 'folderId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Folder')} />
      ),
      cell: ({ row }) => {
        const folderId = row.original.folderId;
        return (
          <div className="text-left min-w-[150px]">
            {folderId ? (
              <FolderBadge folderId={folderId} />
            ) : (
              <span>{t('Uncategorized')}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'created',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Created')} />
      ),
      cell: ({ row }) => {
        const created = row.original.created;
        return (
          <div className="text-left font-medium min-w-[150px]">
            {formatUtils.formatDate(new Date(created))}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('Status')} />
      ),
      cell: ({ row }) => {
        return (
          <div
            className="flex items-center space-x-2"
            onClick={(e) => e.stopPropagation()}
          >
            <FlowStatusToggle
              flow={row.original}
              flowVersion={row.original.version}
            ></FlowStatusToggle>
          </div>
        );
      },
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="" />
      ),
      cell: ({ row }) => {
        const flow = row.original;
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FlowActionMenu
              insideBuilder={false}
              flow={flow}
              readonly={false}
              flowVersion={flow.version}
              onRename={() => {
                setRefresh(refresh + 1);
                refetch();
              }}
              onMoveTo={() => {
                setRefresh(refresh + 1);
                refetch();
              }}
              onDuplicate={() => {
                setRefresh(refresh + 1);
                refetch();
              }}
              onDelete={() => {
                setRefresh(refresh + 1);
                const newSelectedRows = selectedRows.filter(
                  (selectedRow) => selectedRow.id !== flow.id
                );
                setSelectedRows(newSelectedRows);
                refetch();
              }}
            >
              <EllipsisVertical className="h-10 w-10" />
            </FlowActionMenu>
          </div>
        );
      },
    },
  ];

  const bulkActions = useFlowsBulkActions({
    selectedRows,
    isDropdownOpen,
    setIsDropdownOpen,
    refresh,
    setSelectedRows,
    setRefresh,
    refetch,
  });

  return (
    <div className="flex flex-col gap-4 grow">
      <TaskLimitAlert />
      <div className="flex flex-col gap-4 w-full grow">
        <div className="flex">
          <TableTitle>{t('Flows')}</TableTitle>
          <div className="ml-auto flex flex-row gap-2">
            <FeedbackPopover source={FeedbackSource.FLOWS}>
              <Button
                variant="outline"
                size="sm"
                className="flex gap-2 items-center"
              >
                <MessageSquareText className="w-4 h-4" />
                {t('Feedback')}
              </Button>
            </FeedbackPopover>

            <PermissionNeededTooltip
              hasPermission={doesUserHavePermissionToWriteFlow}
            >
              <ImportFlowDialog
                insideBuilder={false}
                onRefresh={() => {
                  setRefresh(refresh + 1);
                  refetch();
                }}
              >
                <Button
                  disabled={!doesUserHavePermissionToWriteFlow}
                  variant="outline"
                  size="sm"
                  className="flex gap-2 items-center"
                >
                  <Import className="w-4 h-4" />
                  {t('Import Flow')}
                </Button>
              </ImportFlowDialog>
            </PermissionNeededTooltip>

            <PermissionNeededTooltip
              hasPermission={doesUserHavePermissionToWriteFlow}
            >
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger
                  disabled={!doesUserHavePermissionToWriteFlow}
                  asChild
                >
                  <Button
                    disabled={!doesUserHavePermissionToWriteFlow}
                    variant="default"
                    className="flex gap-2 items-center"
                    size="sm"
                    loading={isCreateFlowPending}
                  >
                    <span>{t('New Flow')}</span>
                    <ChevronDown className="h-4 w-4 " />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      createFlow({ isAiBuilder: false });
                    }}
                    disabled={isCreateFlowPending}
                  >
                    <Plus className="h-4 w-4 me-2" />
                    <span>{t('From scratch')}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      createFlow({ isAiBuilder: true });
                    }}
                    disabled={isCreateFlowPending}
                  >
                    <Sparkle className="h-4 w-4 me-2" />
                    <span>{t('Create with AI Builder')}</span>
                  </DropdownMenuItem>
                  <SelectFlowTemplateDialog>
                    <DropdownMenuItem
                      onSelect={(e) => e.preventDefault()}
                      disabled={isCreateFlowPending}
                    >
                      <Workflow className="h-4 w-4 me-2" />
                      <span>{t('Use a template')}</span>
                    </DropdownMenuItem>
                  </SelectFlowTemplateDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </PermissionNeededTooltip>
          </div>
        </div>
        <div className="flex flex-row gap-4">
          <div className='flex flex-col gap-4'>
            {!embedState.hideFolders && <FolderFilterList refresh={refresh} />}
            <UsageOverview />
          </div>
          <div className="w-full">
            <DataTable
              columns={columns.filter(
                (column) =>
                  !embedState.hideFolders || column.accessorKey !== 'folderId',
              )}
              page={data}
              isLoading={isLoading}
              filters={filters}
              bulkActions={bulkActions}
              onRowClick={(row, newWindow) => {
                if (newWindow) {
                  openNewWindow(
                    authenticationSession.appendProjectRoutePrefix(
                      `/flows/${row.id}`,
                    ),
                  );
                } else {
                  navigate(
                    authenticationSession.appendProjectRoutePrefix(
                      `/flows/${row.id}`,
                    ),
                  );
                }
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { FlowsPage };
